import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef } from "react";
import Layout from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { useDestination, API } from "../App";
import { listCaribbeanOnly, listShopDestinations } from "../config/caribbeanDestinations";
import { MarketplaceCard, getMarketplaceConfig } from "../components/MarketplaceLogo";
import { TrustBadges } from "../components/TrustBadges";
import BarbadosDeliveryPreview from "../components/BarbadosDeliveryPreview";
import CrossCountryShippingSection from "../components/CrossCountryShippingSection";
import {
  Globe,
  Shield,
  Truck,
  ArrowRight,
  CreditCard,
  BadgeCheck,
  ShoppingBag,
  Box,
  Link2,
  Search,
} from "lucide-react";

// Supported marketplaces
const marketplaceIds = ["amazon", "alibaba", "aliexpress", "ebay", "temu", "shein"];

const features = [
  {
    icon: Globe,
    title: "6 Global Marketplaces",
    description: "Access millions of products from Amazon, Alibaba, AliExpress, eBay, TEMU & SHEIN — all in one place.",
  },
  {
    icon: Shield,
    title: "One Transparent Price",
    description: "No hidden fees ever. Customs, taxes, freight, and delivery — all included upfront.",
  },
  {
    icon: Truck,
    title: "Door-to-Door Delivery",
    description: "We handle international shipping, customs clearance, and last-mile delivery.",
  },
  {
    icon: BadgeCheck,
    title: "Buyer Protection",
    description: "Secure PayPal checkout with full purchase protection on every order.",
  },
];

const destinations = [
  ...listCaribbeanOnly().map((d) => ({ ...d, popular: ["BB", "TT", "JM", "LC"].includes(d.code) })),
  ...listShopDestinations()
    .filter((d) => d.region === "international")
    .map((d) => ({ ...d, popular: false })),
];

const steps = [
  { 
    step: 1, 
    title: "Browse & Discover", 
    desc: "Search products from 6 global marketplaces. Filter by category, price, or marketplace.",
    icon: ShoppingBag
  },
  { 
    step: 2, 
    title: "See Your Landed Price", 
    desc: "Instantly see the total cost including shipping, customs, taxes — no surprises.",
    icon: CreditCard
  },
  { 
    step: 3, 
    title: "Secure Checkout", 
    desc: "Pay once with PayPal. We purchase from the marketplace and handle everything.",
    icon: Shield
  },
  { 
    step: 4, 
    title: "Delivered to You", 
    desc: "Track your package from warehouse to your doorstep. Customs cleared for you.",
    icon: Box
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { destination, setDestination } = useDestination();
  const howItWorksRef = useRef(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Layout>
      {/* Hero — full-bleed image on every breakpoint (was hidden below lg) */}
      <section
        className="relative text-white overflow-hidden min-h-[100svh] sm:min-h-[90vh] flex items-end sm:items-center"
        data-testid="home-hero"
      >
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="/assets/hero-caribbean-delivery.jpg"
            alt=""
            width={1920}
            height={1276}
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover object-[center_35%] sm:object-center scale-105 sm:scale-100"
          />
          {/* Readable overlays — keep beach visible on mobile, deepen toward copy */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/35 sm:via-slate-950/55 sm:to-slate-950/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-transparent" />
          <div className="absolute -top-32 -right-24 w-[420px] h-[420px] bg-orange-500/20 rounded-full blur-[100px] pointer-events-none" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-16 pt-28 sm:py-28 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl lg:max-w-2xl"
          >
            <p className="mb-5 text-sm sm:text-base font-medium tracking-wide text-orange-200/95">
              Now serving the Caribbean &amp; Beyond
            </p>

            <h1
              className="hero-title font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter mb-5 sm:mb-6 leading-[1.05]"
              data-testid="hero-title"
            >
              Shop from
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-amber-300 to-orange-400 break-keep">
                Any Marketplace.
              </span>
              <span className="block break-keep">Pay One Price.</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-slate-200/90 mb-8 sm:mb-10 max-w-lg leading-relaxed">
              Amazon, Alibaba, AliExpress, eBay, TEMU &amp; SHEIN — delivered to your door with one
              all-inclusive price (customs, shipping, and taxes included).
            </p>

            <div className="flex flex-col xs:flex-row sm:flex-row flex-wrap gap-3 sm:gap-4 mb-8 sm:mb-10">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  onClick={() => navigate("/submit")}
                  className="w-full sm:w-auto bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-400 hover:via-amber-400 hover:to-orange-500 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-xl shadow-xl shadow-orange-500/25"
                  data-testid="cta-submit-link"
                >
                  <Link2 className="mr-2 h-5 w-5 shrink-0" />
                  Submit a Product Link
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  onClick={() => navigate("/products")}
                  className="w-full sm:w-auto bg-white/15 border border-white/40 text-white hover:bg-white/25 backdrop-blur-sm px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-xl"
                  data-testid="cta-shop-now"
                >
                  <Search className="mr-2 h-5 w-5 shrink-0" />
                  Browse Global Picks
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  onClick={() => navigate("/global-shipping")}
                  className="w-full sm:w-auto bg-transparent border border-orange-300/60 text-white hover:bg-orange-500/25 backdrop-blur-sm px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-xl"
                  data-testid="cta-global-shipping"
                >
                  <Globe className="mr-2 h-5 w-5 shrink-0" />
                  Cross-Country Shipping
                </Button>
              </motion.div>
            </div>

            <TrustBadges variant="compact" className="mt-2 [&_*]:text-slate-100" />
          </motion.div>
        </div>
      </section>

      {/* Marketplace Showcase */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
              Shop from the World&apos;s Best Marketplaces
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              We aggregate products from these trusted platforms and deliver them to you with one simple price
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {marketplaceIds.map((mpId, idx) => (
              <motion.div
                key={mpId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08, type: "spring", stiffness: 200 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="transition-all duration-300"
              >
                <MarketplaceCard
                  marketplace={mpId}
                  onClick={() => navigate(`/products?marketplace=${mpId}`)}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CrossCountryShippingSection />

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
              Why Shop With CheepSweepz?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We&apos;ve eliminated the complexity of international shopping. No customs headaches, no surprise fees, no hassle.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, type: "spring", stiffness: 200 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
                className="group bg-slate-50/80 border border-slate-200/80 rounded-2xl p-6 hover:bg-white hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/5 transition-all duration-300"
                data-testid={`feature-${idx}`}
              >
                <div className="w-14 h-14 mb-5 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25 group-hover:shadow-orange-500/30 transition-shadow">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {destination === "BB" && <BarbadosDeliveryPreview />}

      {/* How It Works */}
      <section ref={howItWorksRef} id="how-it-works" className="py-24 md:py-32 bg-slate-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block bg-orange-100 text-orange-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
              SIMPLE 4-STEP PROCESS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
              How CheepSweepz Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From browsing to delivery, we make international shopping effortless
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.12, type: "spring", stiffness: 200 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="relative transition-all duration-300"
                data-testid={`step-${item.step}`}
              >
                <div className="bg-white border border-slate-200 rounded-2xl p-6 h-full hover:border-orange-300 hover:shadow-xl hover:shadow-orange-500/5 transition-all duration-300">
                  <div className="absolute -top-4 left-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-xl flex items-center justify-center font-bold text-sm shadow-lg shadow-orange-500/25">
                      {item.step}
                    </div>
                  </div>
                  <div className="pt-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                      <item.icon className="h-6 w-6 text-slate-700" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-6 w-6 text-orange-400" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-24 md:py-32 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4 tracking-tight">
              We Deliver To Your Country
            </h2>
            <p className="text-lg text-slate-400">
              Select your destination to see accurate all-inclusive delivered prices
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {destinations.map((dest, idx) => (
              <motion.button
                key={dest.code}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.06, type: "spring", stiffness: 200 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setDestination(dest.code);
                  navigate("/products");
                }}
                className={`relative p-6 rounded-2xl text-center transition-all duration-300 ${
                  destination === dest.code
                    ? "bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-xl shadow-orange-500/30"
                    : "bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50"
                }`}
                data-testid={`destination-${dest.code}`}
              >
                {dest.popular && (
                  <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    Popular
                  </span>
                )}
                <span className="text-4xl mb-3 block">{dest.flag}</span>
                <span className="text-sm font-medium block">{dest.name}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-amber-200 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-6 tracking-tight">
              Ready to Shop the World?
            </h2>
            <p className="text-lg text-orange-50 mb-10 max-w-2xl mx-auto">
              Join thousands of shoppers who trust CheepSweepz Concierge for hassle-free international shopping.
              Your first purchase is just a click away.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  onClick={() => navigate("/products")}
                  className="bg-white text-orange-600 hover:bg-orange-50 px-10 py-6 text-lg rounded-xl shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="cta-bottom"
                >
                  <ShoppingBag className="mr-2 h-5 w-5" />
                  Browse Products
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/register")}
                  className="border-2 border-white text-white hover:bg-white/15 px-10 py-6 text-lg rounded-xl backdrop-blur-sm transition-all duration-300"
                >
                  Create Free Account
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
